/**
 * The language every API call carries.
 *
 * API calls send the reader's language as `Accept-Language`, so content the API can localize
 * (CMS entries, occupation titles, video captions, …) comes back in that language, and anything it
 * cannot localize falls back to English. Where the language comes from depends on who is calling:
 *
 * - In the browser (the SPA), the reader's stored choice (`getLocale()`).
 * - On the server (React Server Components), the URL being rendered (`/es/...`). The RSC entry
 *   installs that source with {@link setApiLocaleSource}; it reads the current request, so each
 *   render gets its own language.
 *
 * Every call to our API should go through {@link apiFetch} (or RTK Query's base query, which uses
 * {@link withApiLocale} too), so a new content source only has to read the language on the API side
 * (`resolveRequestLocale` in api/src/utils/localeUtils.ts). See docs/i18n/locale-on-api-calls.md.
 *
 * The language goes as a `locale` query parameter as well as the header: Azure Front Door does not
 * forward `Accept-Language` on routes with caching enabled (dv, stg, gozig.com), and keys its cache by
 * URL. The API reads the parameter first (`localeQueryMiddleware`). English sends no parameter, so
 * English URLs are unchanged.
 */

import { apiUrl } from './appOrigin';
import { DEFAULT_LOCALE, getLocale, type SupportedLocale } from './locale';

let localeSource: () => SupportedLocale = () => (typeof window === 'undefined' ? DEFAULT_LOCALE : getLocale());

/** Replace where the language comes from: the RSC entry points it at the request's URL. */
export function setApiLocaleSource(source: () => SupportedLocale): void {
    localeSource = source;
}

/** The language for this call; English when the source cannot answer (no request in scope, storage blocked). */
export function apiLocale(): SupportedLocale {
    try {
        return localeSource();
    }
    catch {
        return DEFAULT_LOCALE;
    }
}

/**
 * `path` with `locale=<language>` added, for a language other than English: `/api/categories` stays as
 * it is in English and becomes `/api/categories?locale=es` in Spanish. A path that already names a
 * `locale` is left alone.
 */
export function withApiLocale(path: string, locale: string = apiLocale()): string {
    if (locale.toLowerCase().split('-')[0] === DEFAULT_LOCALE) return path;
    const hashAt = path.indexOf('#');
    const base = hashAt === -1 ? path : path.slice(0, hashAt);
    const hash = hashAt === -1 ? '' : path.slice(hashAt);
    const queryAt = base.indexOf('?');
    if (queryAt !== -1 && new URLSearchParams(base.slice(queryAt + 1)).has('locale')) return path;
    return `${base}${queryAt === -1 ? '?' : '&'}locale=${encodeURIComponent(locale)}${hash}`;
}

/**
 * `fetch` for our API: resolves the path against the API origin and sends the reader's language, as
 * `Accept-Language` (unless the caller set one) and as `?locale=` (see {@link withApiLocale}).
 */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers);
    if (!headers.has('Accept-Language')) headers.set('Accept-Language', apiLocale());
    // The parameter follows the header, so a caller that names a language gets it in both.
    return fetch(apiUrl(withApiLocale(path, headers.get('Accept-Language') ?? apiLocale())), { ...init, headers });
}
